import { Link, useParams } from "react-router-dom";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { ArchiveIcon, ChartBarIcon, CircleCheckIcon, ExternalLinkIcon, FilePlusIcon, FileTextIcon, PencilIcon, RocketIcon, SettingsIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Chip } from "@/components/badge/chip";
import {
  DetailAside,
  DetailColumns,
  DetailMain,
} from "@/components/detail-columns";
import { Property, PropertyList } from "@/components/detail/property";
import { ListEmpty, ListLoading } from "@/components/list/status-text";
import { PageBreadcrumb } from "@/components/page-breadcrumb";
import { ProseMarkdown } from "@/components/prose-markdown";
import { Section } from "@/components/section";
import RcTaskNodeActions from "@/components/rcs/rc-task-node-actions";
import {
  ConclusionInline,
  ReportTransitionForm,
  FirebaseLinkInline,
} from "@/components/rcs/rc-node-forms";
import { UserChip } from "@/components/user-chip";
import { useApp } from "@/hooks/apps";
import { useToast } from "@/hooks/use-toast";
import { toastError } from "@/lib/toast";
import { cn } from "@/lib/utils";
import { mutateRequest, postFetcher, useAuthStore, type User } from "@/lib/swr";
import {
  fmtRcDate,
  isLegacyRc,
  isRcTaskAssignee,
  rcStageBadge,
  RC_TYPES,
  RcRow,
  RcTask,
  rcConclusionTone,
  refreshRcTasks,
} from "@/constants/rc";
import { buildArpuLink } from "./rc-arpu";
import { RcFormDialog, refreshRcs } from "./rc-form-dialog";

const ExternalLink = ({
  href,
  icon,
  children,
}: {
  href: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer"
    className="flex min-w-0 items-center gap-1.5 text-sm text-muted-foreground hover:text-primary"
  >
    {icon}
    <span className="min-w-0 truncate">{children}</span>
  </a>
);

// One node of the review flow below. `state` drives the marker color:
// done = walked through, pending = waiting on a person (amber), todo = not yet.
type FlowStep = {
  key: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  state: "done" | "pending" | "todo" | "rejected";
  detail?: React.ReactNode;
  /** Pending-task actions (confirm / reject / due) inline on the node. */
  actions?: React.ReactNode;
};

// 时间线节点 = 固定管线里的状态节点,与列表 tab 同一套词表(待审批/
// 待配置/实验中·等待数据回收/已部署/存档/已回滚)。App 的 flow 配置只配
// 处理人,不改流程,所以这里不看它。RC 的实际走位(server 下发的 stage)
// 决定每个节点 done/pending/todo;申请提交是流程起点的动作说明,不是节点。
// 实验中节点 = 实验跑动 + 到期数据回收 + 决策(合并前的「等待数据回收」)。
const FLOW_NODE_ORDER = [
  "approve",
  "implement",
  "experimenting",
  "deployed",
  "archived",
  "closed",
] as const;
type FlowNodeKey = (typeof FLOW_NODE_ORDER)[number];

// 每类任务对应的动作词(摘要用)
const TASK_VERB: Record<string, string> = {
  approve: "审批",
  implement: "配置",
  "deploy-approve": "审批",
  deploy: "部署",
  "close-approve": "审批",
  close: "回收线上配置",
};

const FLOW_NODE_ICONS: Record<FlowNodeKey, FlowStep["icon"]> = {
  approve: CircleCheckIcon,
  implement: SettingsIcon,
  experimenting: RocketIcon,
  deployed: RocketIcon,
  archived: ArchiveIcon,
  closed: ArchiveIcon,
};

const buildFlow = (
  rc: RcRow,
  tasks: RcTask[] | undefined,
  userName: (id?: string | null) => string | undefined
): FlowStep[] => {
  const approve = tasks?.find((t) => t.type === "approve");
  const implement = tasks?.find((t) => t.type === "implement");
  const deployApprove = tasks?.find((t) => t.type === "deploy-approve");
  const deploy = tasks?.find((t) => t.type === "deploy");
  const closeApprove = tasks?.find((t) => t.type === "close-approve");
  const close = tasks?.find((t) => t.type === "close");
  const isAb = rc.type === "AB_EXPERIMENT";
  // 数据 report 的交付物:三项填齐 = 数据已回收
  const reportFilled = !!(rc.conclusion && rc.reportDocLink && rc.arpuLink);
  // 到期只影响提示与「链接必填」,不改流程位置
  const overdue = !!rc.endDate && new Date(rc.endDate) < new Date();

  // 走位:server 下发的 stage(与列表徽章同口径);缺省按 status 回退
  const stage: FlowNodeKey =
    rc.stage && (FLOW_NODE_ORDER as readonly string[]).includes(rc.stage)
      ? (rc.stage as FlowNodeKey)
      : rc.status === "CLOSED"
      ? "closed"
      : rc.status === "ARCHIVED"
      ? "archived"
      : rc.status === "EXPERIMENTING"
      ? "experimenting"
      : rc.status === "DEPLOYED"
      ? "deployed"
      : "implement";
  const stageIdx = FLOW_NODE_ORDER.indexOf(stage);
  const terminal = stage === "archived" || stage === "closed";

  // 摘要说清「在等谁做什么」/「谁做完了什么」,而不是笼统的「待确认」——
  // 发起人看的是进度,不是任务系统的内部状态
  const taskDetail = (task: RcTask | undefined): React.ReactNode => {
    if (!task) return undefined;
    const verb = TASK_VERB[task.type] ?? "处理";
    if (task.confirmedAt) {
      const who = userName(task.confirmedBy);
      return `${fmtRcDate(task.confirmedAt)} · ${who ?? "系统"}已${verb}`;
    }
    const who =
      (task.assignees ?? []).map((a) => a.name).join("、") || "未指派";
    return `等待 ${who} ${verb} · 截止 ${fmtRcDate(task.dueAt)}`;
  };

  const applyStep: FlowStep = {
    key: "apply",
    label: "申请提交",
    icon: FilePlusIcon,
    state: "done",
    detail: `${fmtRcDate(rc.createdAt)} · ${rc.requester?.name ?? "—"}`,
  };

  // 审批被拒 = 流程终止(flow 里的 rejected 终态):只展示到拒绝节点
  if (approve?.rejectedAt) {
    return [
      applyStep,
      {
        key: "approve",
        label: "被拒",
        icon: CircleCheckIcon,
        state: "rejected",
        detail: `审批拒绝 ${fmtRcDate(approve.rejectedAt)} · ${
          userName(approve.confirmedBy) ?? "审批人"
        }${approve.rejectReason ? ` · ${approve.rejectReason}` : ""}`,
      },
    ];
  }

  // 流程是固定管线,画哪些节点只看这条 RC 的实际情况(不再看 App 的
  // flow 配置——那里只配处理人,不改流程)
  const nodes = FLOW_NODE_ORDER.filter((node) => {
    switch (node) {
      case "approve":
        // 只画实际走过审批的(任务存在);未配审批/历史行不画
        return !!approve;
      case "implement":
        return true;
      case "experimenting":
        // 参数类不经实验
        return isAb;
      case "deployed":
        // AB 未转部署就回滚的,部署节点没走过,不画
        return !(stage === "closed" && isAb);
      case "archived":
        // 终态到达才画;或当前已部署(存档是它的去向之一)
        return stage === "archived" || stage === "deployed";
      case "closed":
        // 存档终态下只有真发起了回滚才画
        return stage === "archived" ? !!close : true;
    }
  });

  const steps: FlowStep[] = [applyStep];
  for (const node of nodes) {
    const idx = FLOW_NODE_ORDER.indexOf(node);
    const walked = terminal ? idx <= stageIdx : idx < stageIdx;
    let state: FlowStep["state"] =
      walked || (node === stage && terminal)
        ? "done"
        : node === stage
        ? "pending"
        : "todo";
    // 回滚/部署(审批或执行)任务在途时,对应节点标 pending
    if (
      node === "closed" &&
      ((close && !close.confirmedAt) ||
        (closeApprove && !closeApprove.confirmedAt))
    ) {
      state = "pending";
    }
    // AB 回滚收尾还缺结论:该节点标 pending,操作面板给补填入口
    if (
      node === "closed" &&
      isAb &&
      rc.status === "CLOSED" &&
      !rc.conclusion
    ) {
      state = "pending";
    }
    if (
      node === "deployed" &&
      ((deploy && !deploy.confirmedAt) ||
        (deployApprove && !deployApprove.confirmedAt))
    ) {
      state = "pending";
    }
    let label: string = {
      approve: "待审批",
      implement: "待配置",
      experimenting: "实验中·等待数据回收",
      deployed: "已部署",
      archived: "存档",
      closed: "已回滚",
    }[node];
    // 节点标签跟着「当前在等什么」走:还没走到终态时别写终态名
    // (原先卡在部署审批/待操作人部署,节点却写着「已部署」,极易误读)
    if (node === "deployed") {
      if (deployApprove && !deployApprove.confirmedAt) label = "待部署审批";
      else if (deploy && !deploy.confirmedAt) label = "待部署";
    }
    if (node === "closed") {
      if (closeApprove && !closeApprove.confirmedAt) label = "待回滚审批";
      else if (close && !close.confirmedAt) label = "待回收线上配置";
    }
    // 历史迁移行的关闭没有回滚语义,沿用历史词
    if (node === "closed" && isLegacyRc(rc.id) && !close) label = "已关闭";
    let detail: React.ReactNode;
    let actions: React.ReactNode;
    if (node === "approve") {
      detail = taskDetail(approve);
      if (approve) actions = <RcTaskNodeActions task={approve} />;
    } else if (node === "implement") {
      detail = taskDetail(implement);
      if (implement) actions = <RcTaskNodeActions task={implement} />;
    } else if (node === "experimenting") {
      // 当前停在这个节点时,补一句数据回收进度(到期/已回收)
      const here = node === stage;
      const suffix = !here
        ? ""
        : reportFilled
        ? " · 数据已回收"
        : overdue
        ? " · 已到期,待回收数据"
        : "";
      detail =
        idx <= stageIdx && rc.status !== "NOT_STARTED"
          ? `${fmtRcDate(rc.startDate)} → ${fmtRcDate(rc.endDate)}${suffix}`
          : undefined;
    } else if (node === "deployed") {
      // 部署申请在途:先审批(通过/拒绝)再执行——节点上内联对应任务的按钮
      if (deployApprove && !deployApprove.confirmedAt) {
        detail = (
          <span className="text-destructive">{taskDetail(deployApprove)}</span>
        );
        actions = <RcTaskNodeActions task={deployApprove} />;
      } else if (deploy && !deploy.confirmedAt) {
        detail = <span className="text-destructive">{taskDetail(deploy)}</span>;
        actions = <RcTaskNodeActions task={deploy} />;
      } else {
        detail =
          stage === "deployed"
            ? "配置全量上线中,待存档或发起回滚"
            : stage === "archived"
            ? "已全量上线"
            : undefined;
      }
    } else if (node === "archived") {
      detail = stage === "archived" ? "配置保留在线上,流程归档" : undefined;
    } else if (node === "closed") {
      // 回滚申请在途:先审批(通过/拒绝)再回收——节点上内联对应任务的按钮
      if (closeApprove && !closeApprove.confirmedAt) {
        detail = (
          <span className="text-destructive">{taskDetail(closeApprove)}</span>
        );
        actions = <RcTaskNodeActions task={closeApprove} />;
      } else if (close && !close.confirmedAt) {
        detail = <span className="text-destructive">{taskDetail(close)}</span>;
        actions = <RcTaskNodeActions task={close} />;
      } else {
        detail =
          isAb && rc.status === "CLOSED" && !rc.conclusion
            ? "待补结论(看 ARPU 后填写)"
            : taskDetail(close);
      }
    }
    steps.push({
      key: node,
      label,
      icon: FLOW_NODE_ICONS[node],
      state,
      detail,
      actions,
    });
  }

  return steps;
};

const FLOW_MARKER: Record<FlowStep["state"], string> = {
  done: "border-primary text-primary",
  pending: "border-amber-500 text-amber-600",
  todo: "border-muted-foreground/30 text-muted-foreground/30",
  rejected: "border-destructive text-destructive",
};

// 左侧只读 stepper:节点状态与摘要;操作集中到右侧当前节点面板
const RcFlowTimeline = ({ steps }: { steps: FlowStep[] }) => (
  <ol className="relative ml-3 space-y-4 border-l border-border py-1">
    {steps.map((s) => (
      <li key={s.key} className="relative pl-5">
        <span
          className={cn(
            "absolute -left-[13px] flex h-6 w-6 items-center justify-center rounded-full border bg-background",
            FLOW_MARKER[s.state]
          )}
        >
          <s.icon className="h-3 w-3" />
        </span>
        <div
          className={cn(
            "text-sm font-medium",
            s.state === "todo" && "text-muted-foreground/50"
          )}
        >
          {s.label}
        </div>
        {s.detail && (
          <div className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
            {s.detail}
          </div>
        )}
      </li>
    ))}
  </ol>
);

// 右侧当前节点面板:第一个待处理节点的表单与操作按钮
const RcNodePanel = ({ step }: { step: FlowStep }) => (
  <div className="w-full min-w-0 rounded-lg border bg-muted/30 p-4 md:max-w-md md:flex-1">
    <div className="flex items-center gap-2 text-sm font-medium">
      <step.icon className="h-4 w-4 text-amber-600" />
      {step.label}
    </div>
    {step.detail && (
      <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
        {step.detail}
      </div>
    )}
    <div className="mt-3">{step.actions}</div>
  </div>
);

// RC config detail (/rcs/:id): full properties + config doc, the single place
// an RC is manually edited (the list rows just link here).
const RcDetail = () => {
  const { id } = useParams();
  const { toast } = useToast();
  const me = useAuthStore((s) => s.auth?.user);
  const key = id ? `/remote-configs/${id}` : null;
  const { data: rc, isLoading } = useSWR<RcRow>(key ? [key] : null);
  const app = useApp(rc?.appId);
  // Operators are stored as {userId, name} snapshots without avatars —
  // resolve avatars via the global user list (SWR dedupes the fetch).
  const { data: users } = useSWR<User[]>(["/users"]);
  // Same key as RcTasksSection / the dialog — SWR dedupes.
  const { data: tasks } = useSWR<RcTask[]>(
    id ? [`/remote-configs/${id}/tasks`] : null
  );
  // "Initiate close" surfaced on the flow — files a close task; the RC only
  // turns CLOSED once an operator confirms it (same semantics as the dialog).
  const { trigger: closeRc, isMutating: closing } = useSWRMutation(
    `/remote-configs/${id}/close`,
    postFetcher,
    {
      onSuccess: () => {
        toast({
          title: "回滚任务已创建",
          description: "等待操作人回收确认。",
        });
        refreshRcTasks(id);
        refreshRcs();
        if (key) mutateRequest(key);
      },
      onError: (error: Error) =>
        toastError(toast, "发起回滚失败", error),
    }
  );

  // 存档:配置长期保留在线上,流程归档(仅已部署可存档)
  const { trigger: archiveRc, isMutating: archiving } = useSWRMutation(
    `/remote-configs/${id}/archive`,
    postFetcher,
    {
      onSuccess: () => {
        toast({ title: "已存档" });
        refreshRcs();
        if (key) mutateRequest(key);
      },
      onError: (error: Error) => toastError(toast, "存档失败", error),
    }
  );

  if (isLoading) return <ListLoading>Loading RC…</ListLoading>;
  if (!rc) return <ListEmpty>RC config not found.</ListEmpty>;

  const userById = new Map((users ?? []).map((u) => [String(u.id), u]));
  // 角色分工:流转发起(部署申请/发起回滚/存档)与数据 report 回填属于
  // 需求方;操作人只做任务的执行与确认。历史行没 requesterId 的不拦;
  // admin 保留解卡通道(与服务端 assertRequester 同口径)。
  const isRequester =
    !rc.requesterId || me?.id === rc.requesterId || !!me?.isAdmin;
  // flow 配置的节点处理人(具体 userId;「发起人」哨兵已被 isRequester 覆盖)
  const nodeHandlerHasMe = (node: string) =>
    !!me?.id && (app?.rcFlow?.handlers?.[node] ?? []).includes(me.id);
  // 数据 report 的回填人:需求方,或配置在等待数据回收节点的处理人(BI
  // 出数据的团队);结论补填同理挂 closed 节点。流转发起仍只属于需求方。
  // 存量:数据回填人曾配在合并前的 awaiting-data 节点上,一并认
  const canFillReport =
    isRequester ||
    nodeHandlerHasMe("experimenting") ||
    nodeHandlerHasMe("awaiting-data");
  const rcOverdue = !!rc.endDate && new Date(rc.endDate) < new Date();
  const canFillConclusion = isRequester || nodeHandlerHasMe("closed");
  const isAbRc = rc.type === "AB_EXPERIMENT";
  // 人工回填的查数终版链接优先;缺省用公式推导的 Friday 深链起点
  const arpuLink = rc.arpuLink || buildArpuLink(rc);
  const flow = buildFlow(
    rc,
    tasks,
    (uid) => (uid ? userById.get(String(uid))?.name : undefined)
  );
  const pendingClose = !!tasks?.some(
    (t) => (t.type === "close" || t.type === "close-approve") && !t.confirmedAt
  );
  const pendingDeploy = !!tasks?.some(
    (t) =>
      (t.type === "deploy" || t.type === "deploy-approve") && !t.confirmedAt
  );
  // 上一次部署申请被拒(且未重提)时,把理由亮给需求方,修订后可重新提交
  const rejectedDeployReview = tasks?.find(
    (t) => t.type === "deploy-approve" && t.rejectedAt
  );
  const rejectedCloseReview = tasks?.find(
    (t) => t.type === "close-approve" && t.rejectedAt
  );
  const closeRejectedHint = rejectedCloseReview && (
    <span className="text-xs text-destructive">
      上次回滚申请被拒
      {rejectedCloseReview.rejectReason
        ? `:${rejectedCloseReview.rejectReason}`
        : ""}
      ,可重新发起
    </span>
  );
  const deployRejectedHint = rejectedDeployReview && (
    <span className="text-xs text-destructive">
      上次部署申请被拒
      {rejectedDeployReview.rejectReason
        ? `:${rejectedDeployReview.rejectReason}`
        : ""}
      ,修订后可重新提交
    </span>
  );

  // Stepper 化:每个 pending 节点把"该干的事"内联在节点上,并按角色分工——
  // 流转发起(部署申请/发起回滚/存档)与数据 report 回填只给需求方;
  // 任务级按钮(确认/拒绝)由 buildFlow 挂 RcTaskNodeActions,只对
  // assignee(操作人/审批人)显示。
  const refreshAfterFlow = () => {
    refreshRcTasks(id);
    refreshRcs();
    if (key) mutateRequest(key);
  };
  const closeButton = (
    <Button
      variant="outline"
      size="sm"
      className="gap-1.5"
      disabled={closing}
      onClick={() => closeRc({})}
      title="创建回滚任务,操作人回收线上配置后确认"
    >
      <ArchiveIcon className="h-3.5 w-3.5" />
      发起回滚
    </Button>
  );
  const nodeActions = (s: FlowStep): React.ReactNode => {
    // 回滚/部署任务在途时,发起类入口全部收起(任务确认按钮在对应节点上)
    if (s.state !== "pending" || pendingClose || pendingDeploy) {
      return undefined;
    }
    if (s.key === "experimenting" && rc.status === "EXPERIMENTING" && canFillReport) {
      // 实验中节点承载 report 回填 + 决策:结论是分支开关,一次点击 =
      // 保存 report + 流转(正向→部署申请,持平→部署/回滚,负向→回滚)。
      // 流程只由这些按钮推进;endDate 只用于提醒与「到期后链接必填」。
      // 回填人 = 需求方或节点处理人(BI),流转只给需求方(BI 只见保存)。
      return (
        <div className="mt-1.5 flex flex-col items-start gap-1.5">
          {arpuLink && (
            <a
              href={arpuLink}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-muted-foreground hover:text-primary"
            >
              查实验数据(ARPU dashboard)↗
            </a>
          )}
          {isRequester && deployRejectedHint}
          {isRequester && closeRejectedHint}
          <ReportTransitionForm
            rc={rc}
            canTransition={isRequester}
            requireLinks={rcOverdue}
            onDone={() => {
              refreshAfterFlow();
              mutateRequest("/remote-configs/conclusion-todo");
            }}
          />
        </div>
      );
    }
    if (s.key === "deployed" && rc.status === "DEPLOYED" && isRequester) {
      return (
        <div className="mt-1.5 flex items-center gap-1.5">
          {/* 部署收尾的两个去向(需求方定夺):存档(配置保留)或发起回滚(下线) */}
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            disabled={archiving}
            onClick={() => archiveRc({})}
            title="配置长期保留在线上,流程归档"
          >
            <ArchiveIcon className="h-3.5 w-3.5" />
            存档
          </Button>
          {closeButton}
        </div>
      );
    }
    // 回滚后补结论(AB 提前快停等未在实验中填的情况)
    if (
      s.key === "closed" &&
      isAbRc &&
      rc.status === "CLOSED" &&
      !rc.conclusion &&
      canFillConclusion
    ) {
      return (
        <ConclusionInline
          rcId={rc.id}
          onSaved={() => {
            if (key) mutateRequest(key);
            refreshRcs();
            mutateRequest("/remote-configs/conclusion-todo");
          }}
        />
      );
    }
    return undefined;
  };
  const implementTask = tasks?.find((t) => t.type === "implement");
  const steps = flow.map((s) => {
    let actions = s.actions ?? nodeActions(s);
    // AB 落地:操作人(落地任务 assignee)贴上 Firebase 链接 = 实验已开,
    // 保存即自动确认落地任务、状态进实验中(一步到位);历史行没有落地
    // 任务的保持"谁有链接谁填"(纯保存)。节点上的操作只给节点的人——
    // admin 不渲染,代填/解卡统一走 Edit 对话框(admin 专属字段 + Flow tasks)
    if (
      s.key === "implement" &&
      s.state === "pending" &&
      rc.type === "AB_EXPERIMENT" &&
      !rc.rcFirebaseLink &&
      (!implementTask || isRcTaskAssignee(implementTask, me?.id))
    ) {
      const canConfirm =
        implementTask &&
        !implementTask.confirmedAt &&
        isRcTaskAssignee(implementTask, me?.id);
      // 「保存并开始实验」本身就完成了确认,不再并排一个 Confirm 按钮
      actions = (
        <>
          <FirebaseLinkInline
            rcId={rc.id}
            confirmTaskId={canConfirm ? implementTask.id : undefined}
            onSaved={refreshAfterFlow}
          />
          {canConfirm ? null : actions}
        </>
      );
    }
    return { ...s, actions };
  });

  return (
    <div className="space-y-6">
      <PageBreadcrumb
        parent={{ label: "RC Configs", to: "/rcs" }}
        current={rc.name}
      />

      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1.5">
          <h1 className="text-2xl font-semibold leading-tight">{rc.name}</h1>
          <div className="flex flex-wrap items-center gap-1.5">
            <Chip tone={RC_TYPES[rc.type]?.tone ?? "neutral"}>
              {RC_TYPES[rc.type]?.label ?? rc.type}
            </Chip>
            <Chip tone={rcStageBadge(rc).tone}>{rcStageBadge(rc).label}</Chip>
            {rc.conclusion && (
              <Chip tone={rcConclusionTone(rc.conclusion)}>{rc.conclusion}</Chip>
            )}
          </div>
        </div>
        <RcFormDialog
          rc={rc}
          onSaved={() => mutateRequest(key!)}
          trigger={
            <Button variant="outline" size="sm" className="h-7 gap-1.5 px-2 text-xs">
              <PencilIcon className="h-3.5 w-3.5" /> Edit
            </Button>
          }
        />
      </header>

      <DetailColumns>
        <DetailMain>
          <div className="space-y-6">
            <Section title="审核流程">
              <div className="flex flex-col gap-5 md:flex-row md:items-start">
                <div className="shrink-0 md:min-w-56">
                  <RcFlowTimeline steps={steps} />
                </div>
                {(() => {
                  const active = steps.find(
                    (s) => s.state === "pending" && s.actions
                  );
                  return active ? <RcNodePanel step={active} /> : null;
                })()}
              </div>
            </Section>
            {rc.configDoc && (
              <Section title="Config doc">
                <ProseMarkdown content={rc.configDoc} />
              </Section>
            )}
            {rc.remarks && (
              <Section title="Remarks">
                <p className="whitespace-pre-wrap text-sm">{rc.remarks}</p>
              </Section>
            )}
            {rc.deployNote && (
              <Section title="Deploy note">
                <p className="whitespace-pre-wrap text-sm">{rc.deployNote}</p>
              </Section>
            )}
          </div>
        </DetailMain>
        <DetailAside>
          <PropertyList>
            <Property label="App">
              {app ? (
                <Link to={`/apps/${app.id}/rcs`} className="hover:text-primary">
                  {app.name}
                </Link>
              ) : (
                rc.appId
              )}
            </Property>
            <Property label="Requester">
              {rc.requester ? (
                <Link
                  to={`/users/${rc.requester.id}/prds`}
                  className="flex min-w-0 items-center hover:text-primary"
                >
                  <UserChip user={rc.requester} />
                </Link>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </Property>
            <Property label="Operators">
              {rc.operators?.length ? (
                <div className="flex flex-col gap-1.5">
                  {rc.operators.map((o, i) => (
                    <UserChip
                      key={o.userId ?? `${o.name}-${i}`}
                      user={{
                        name: o.name,
                        avatarThumb: o.userId
                          ? userById.get(o.userId)?.avatarThumb
                          : undefined,
                      }}
                    />
                  ))}
                </div>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </Property>
            <Property label="Countries">
              {(rc.countries ?? []).join(", ") || "—"}
            </Property>
            <Property label="Modules">
              {(rc.modules ?? []).join(", ") || "—"}
            </Property>
            <Property label="Dates">
              <span className="tabular-nums">
                {fmtRcDate(rc.startDate)}
                {" → "}
                {fmtRcDate(rc.endDate)}
              </span>
            </Property>
            <Property label="Links">
              <div className="flex flex-col gap-1">
                {rc.docLink && (
                  <ExternalLink
                    href={rc.docLink}
                    icon={<FileTextIcon className="h-3.5 w-3.5 shrink-0" />}
                  >
                    RC doc
                  </ExternalLink>
                )}
                {rc.rcFirebaseLink && (
                  <ExternalLink
                    href={rc.rcFirebaseLink}
                    icon={<ExternalLinkIcon className="h-3.5 w-3.5 shrink-0" />}
                  >
                    Experiment (Firebase)
                  </ExternalLink>
                )}
                {arpuLink && (
                  <ExternalLink
                    href={arpuLink}
                    icon={<ChartBarIcon className="h-3.5 w-3.5 shrink-0" />}
                  >
                    ARPU dashboard
                  </ExternalLink>
                )}
                {rc.reportDocLink && (
                  <ExternalLink
                    href={rc.reportDocLink}
                    icon={<FileTextIcon className="h-3.5 w-3.5 shrink-0" />}
                  >
                    Data report
                  </ExternalLink>
                )}
                {rc.relatedTaskOrPrdLink && (
                  <ExternalLink
                    href={rc.relatedTaskOrPrdLink}
                    icon={<ExternalLinkIcon className="h-3.5 w-3.5 shrink-0" />}
                  >
                    Related task / PRD
                  </ExternalLink>
                )}
                {!rc.docLink &&
                  !rc.rcFirebaseLink &&
                  !arpuLink &&
                  !rc.reportDocLink &&
                  !rc.relatedTaskOrPrdLink && (
                    <span className="text-muted-foreground">—</span>
                  )}
              </div>
            </Property>
          </PropertyList>
        </DetailAside>
      </DetailColumns>
    </div>
  );
};

export default RcDetail;
